import { useAuth } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import useCredits from "features/User/hooks/useCredits";
import { getYesNoAnalysis } from "helpers/api";

/**
 * Hook to fetch consensus meter summary for a given search id or return details about the user that
 * prevented the summary from being fetched.
 *
 * @returns Summary of results for each category in the consensus meter or details about the user that prevented the summary from being fetched
 */
export const useConsensusSnapshot = (searchId?: string) => {
  const {
    isOutOfSnapshotCredits,
    consensusSnapshotWasPreviouslyRunOnQuery,
    isPremium,
    spendCreditsOnConsensusMeterSnapshot,
  } = useCredits(searchId?.toString());

  const { isSignedIn, isLoaded } = useAuth();

  const isOutOfCredits =
    !isPremium &&
    isOutOfSnapshotCredits &&
    !consensusSnapshotWasPreviouslyRunOnQuery;

  const isNotSignedIn = !isSignedIn && isLoaded;

  const isEnabled = !isOutOfCredits && !isNotSignedIn;

  const { data, error, isLoading } = useQuery(
    ["consensusSnapshot", searchId],
    async () => {
      const analysisOutput = await getYesNoAnalysis({
        threadSearchId: searchId!,
      });

      return analysisOutput;
    },
    {
      enabled: isEnabled && !!searchId,
      staleTime: Infinity,
      onSuccess: () => {
        if (
          isLoaded &&
          isSignedIn &&
          !consensusSnapshotWasPreviouslyRunOnQuery &&
          !isPremium
        ) {
          spendCreditsOnConsensusMeterSnapshot();
        }
      },
    }
  );

  return {
    summary: data,
    summaryError: error,
    isLoading: isLoading && isEnabled,
    isOutOfCredits,
    isNotSignedIn,
  };
};
